import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { NotifierType, useNotifier } from 'hooks/use-notifier';
import store from 'store';
import { useGetJobStatusQuery } from 'store/api/job_statuses/job_statuses.api';
import { removeJob, useGlobalRivalJobs } from 'store/views/global/rivals';

import type { RootState } from 'store/store.types';

const POLLING_INTERVAL = 10000;
const NOTIFIER_DURATION = 5;

type CreateRivalNotifierProps = {
  rivalId: number;
  id: string;
  silent?: boolean;
};
function CreateRivalNotifier({
  id,
  rivalId,
  silent,
}: CreateRivalNotifierProps) {
  const { t } = useTranslation();
  const [pollingInterval, setPollingInterval] =
    useState<number>(POLLING_INTERVAL);
  const { data: jobStatus } = useGetJobStatusQuery(id, { pollingInterval });
  const { notify } = useNotifier();

  const rival = useSelector((state: RootState) =>
    store.select.rivals.byId(state).get(rivalId.toString()),
  );

  const notifiedStart = useRef(false);
  useEffect(() => {
    if (silent) return;
    if (rival && !notifiedStart.current) {
      notify({
        message: t('Common:boardWizard.createRivalsNotifier.start', {
          name: rival.name,
        }),
        type: NotifierType.INFO,
        duration: NOTIFIER_DURATION,
      });
      notifiedStart.current = true;
    }
  }, [notify, rival, silent, t]);

  const notifiedComplete = useRef(false);
  useEffect(() => {
    if (!jobStatus || notifiedComplete.current) return;
    // `failedAt` is also terminal — without handling it, a failed job
    // stays in globalRivals.jobs and keeps polling every 10s forever.
    // A CSV batch of N failures would leak N pollers.
    const isTerminal = !!jobStatus.completedAt || !!jobStatus.failedAt;
    if (!isTerminal) return;

    // For a non-silent SUCCESS we key the toast on the rival's name,
    // so we must hold the job (keep polling paused? no — completed
    // means the BE is done) until the rival lands in the store.
    // Otherwise the removeJob below tears down this component before
    // the rival update arrives and the legacy BoardWizard loses its
    // success toast entirely. Fail/silent branches don't need `rival`
    // and can terminate immediately.
    const isSuccess = !!jobStatus.completedAt && !jobStatus.failedAt;
    if (!silent && isSuccess && !rival) return;

    if (isSuccess) {
      // Silent-mode SUCCESS: modal owns the completion view; suppress
      // toast. Non-silent: rival is present by the guard above.
      if (!silent && rival) {
        notify({
          message: t('Common:boardWizard.createRivalsNotifier.complete', {
            name: rival.name,
          }),
          type: NotifierType.SUCCESS,
          duration: NOTIFIER_DURATION,
        });
      }
    } else {
      // FAILURE. Surface it in both modes — silent-mode callers (the
      // new-board modal) only await the POST response and never
      // observe the async job outcome, so without this toast a
      // background job failure would leave the modal reporting the
      // board as created while it silently failed on the BE. Use the
      // unnamed key when `rival` never landed to avoid rendering an
      // empty placeholder.
      notify({
        message: rival
          ? t('Common:boardWizard.createRivalsNotifier.failed', {
              name: rival.name,
            })
          : t('Common:boardWizard.createRivalsNotifier.failedUnnamed'),
        type: NotifierType.ERROR,
        duration: NOTIFIER_DURATION,
      });
    }
    notifiedComplete.current = true;
    removeJob(jobStatus.id);
    setPollingInterval(0);
  }, [id, jobStatus, notify, rival, silent, t]);
  return null;
}

export function CreateRivalsNotifier() {
  const jobs = useGlobalRivalJobs();
  return (
    <>
      {jobs.map((job) => (
        // key by job.id — without it, removing the first job when it
        // completes lets React reuse this instance for the next job,
        // which inherits notifiedComplete=true and pollingInterval=0
        // and never polls / never gets removed from globalRivals.jobs.
        <CreateRivalNotifier key={job.id} {...job} />
      ))}
    </>
  );
}
